/** Which coverage cell a file is about to turn - and the honest answer when that cannot be said.
 *
 * The census (`/sources`) is per expected source per window. A document is not tied to a source:
 * nothing a document says is resolved to a row (SEC-05), and the match is a person's, made in review.
 * So this answers only when the evidence is unambiguous - exactly **one** expected source at the
 * site whose category fits the document and whose window overlaps the document's period - and
 * answers `null` otherwise. A cell that guessed would be a claim about coverage the product has not
 * made, on the screen that decides whether a filing is complete.
 */

import type { SourceCoverage, SourceWindow } from "../../api/sources";

export interface CoverageTarget {
  source: SourceCoverage;
  window: SourceWindow;
}

/** The census categories a document kind can feed. A kind that fits several (a fuel invoice) or
 * none (a certificate, a contract, a spreadsheet) maps to the ones it can feed or to nothing. */
const CATEGORIES_OF_KIND: Readonly<Record<string, readonly string[]>> = {
  electricity_bill: ["electricity"],
  gas_bill: ["stationary"],
  fuel_invoice: ["mobile", "stationary"],
  refrigerant_log: ["fugitive"],
};

const overlaps = (aStart: string, aEnd: string, bStart: string, bEnd: string): boolean =>
  aStart <= bEnd && bStart <= aEnd;

export function coverageCellFor(input: {
  sources: readonly SourceCoverage[];
  facilityId: string | null | undefined;
  /** Known exactly once a figure exists; otherwise the kind decides. */
  category?: string | null;
  kind: string;
  periodStart: string | null | undefined;
  periodEnd: string | null | undefined;
}): CoverageTarget | null {
  const { sources, facilityId, category, kind, periodStart, periodEnd } = input;
  if (!facilityId || !periodStart || !periodEnd) return null;
  const categories = category ? [category] : (CATEGORIES_OF_KIND[kind] ?? []);
  if (categories.length === 0) return null;
  const hits: CoverageTarget[] = [];
  for (const source of sources) {
    if (source.facility_id !== facilityId || !categories.includes(source.category)) continue;
    for (const window of source.windows) {
      if (overlaps(window.period_start, window.period_end, periodStart, periodEnd)) {
        hits.push({ source, window });
      }
    }
  }
  const [only, ...rest] = hits;
  return only && rest.length === 0 ? only : null;
}
